import { SWRConfiguration, SWRResponse } from 'swr';

import { DEFAULT_OPTIONS } from '@/constants/swrDefaultOptions';
import {
  SetSearchParamsOverride,
  useStateFromSearchParams,
} from '@/hooks/useStateFromSearchParams';
import { useSWR } from '@/hooks/useSwr';
import { IdentitySchema, PendingIdentitySchema } from '@/types/schema';
import { apiOrganizationsPath, apiWorkspacesPath } from '@/utils/constants';

import {
  MemberGroupType,
  MemberStatus,
} from '../types/organizationMembers.type';
import { MEMBERS_LIMIT } from './constants';

type MembersResponse<T extends 'active' | 'pending'> =
  | ((T extends 'active' ? IdentitySchema[] : PendingIdentitySchema[]) & {
      headers?: {
        'x-pagination-total': number;
      };
    })
  | undefined;

export const useMembers = <T extends MemberStatus>(
  type: T,
  orgOrWorkspace: MemberGroupType,
  setSearchParams?: SetSearchParamsOverride,
  search?: {
    emails?: string[];
    user_ids?: string[];
    ls_user_ids?: string[];
  }
): {
  members: SWRResponse<MembersResponse<T>, MembersParams, never>;
  total: number;
  page: number;
  setPage: (page: number) => void;
  hasMore: boolean;
} => {
  const [page, setPage] = useStateFromSearchParams(
    'memberPage',
    '0',
    setSearchParams
  );
  const offset = parseInt(page, 10) * MEMBERS_LIMIT;
  const params = {
    offset,
    limit: MEMBERS_LIMIT,
    ...(search ? { ...search } : {}),
  };

  const activeOrgMembers = useActiveOrganizationMembers(params, {
    skip:
      type === MemberStatus.PENDING ||
      orgOrWorkspace === 'workspace' ||
      Number.isNaN(offset),
  });
  const pendingOrgMembers = usePendingOrganizationMembers(params, {
    skip:
      type === MemberStatus.ACTIVE ||
      orgOrWorkspace === 'workspace' ||
      Number.isNaN(offset),
  });
  const activeWorkspaceMembers = useActiveWorkspaceMembers(params, {
    skip:
      type === MemberStatus.PENDING ||
      orgOrWorkspace === 'organization' ||
      Number.isNaN(offset),
  });
  const pendingWorkspaceMembers = usePendingWorkspaceMembers(params, {
    skip:
      type === MemberStatus.ACTIVE ||
      orgOrWorkspace === 'organization' ||
      Number.isNaN(offset),
  });

  let membersSWR;
  if (orgOrWorkspace === MemberGroupType.ORGANIZATION) {
    membersSWR =
      type === MemberStatus.ACTIVE ? activeOrgMembers : pendingOrgMembers;
  } else {
    membersSWR =
      type === MemberStatus.ACTIVE
        ? activeWorkspaceMembers
        : pendingWorkspaceMembers;
  }
  const total = parseInt(
    membersSWR.data?.headers?.['x-pagination-total'] ?? '0',
    10
  );
  const hasMore = total >= offset + MEMBERS_LIMIT;

  return {
    members: membersSWR,
    total,
    page: parseInt(page, 10),
    setPage: (page: number) => setPage(page.toString()),
    hasMore,
  };
};

export const ACTIVE_PAGE = 0;
export const PENDING_PAGE = 1;

export type MembersParams = {
  offset: number;
  limit: number;

  emails?: string[];
  user_ids?: string[];
  ls_user_ids?: string[];
};

export const useActiveOrganizationMembers = (
  params: MembersParams | null,
  options?: SWRConfiguration & { skip?: boolean }
) => {
  return useSWR<IdentitySchema[], MembersParams, never>(
    options?.skip || !params
      ? null
      : {
          url: `${apiOrganizationsPath}/current/members/active`,
          params,
          method: 'GET',
        },
    { ...DEFAULT_OPTIONS, ...options }
  );
};

export const usePendingOrganizationMembers = (
  params: MembersParams,
  options?: SWRConfiguration & { skip?: boolean }
) => {
  return useSWR<PendingIdentitySchema[], MembersParams, never>(
    options?.skip
      ? null
      : {
          url: `${apiOrganizationsPath}/current/members/pending`,
          method: 'GET',
          params,
        },
    { ...DEFAULT_OPTIONS, ...options }
  );
};

export const useActiveWorkspaceMembers = (
  params: MembersParams,
  options?: SWRConfiguration & { skip?: boolean }
) => {
  return useSWR<IdentitySchema[], MembersParams, never>(
    options?.skip
      ? null
      : {
          url: `${apiWorkspacesPath}/current/members/active`,
          method: 'GET',
          params,
        },
    { ...DEFAULT_OPTIONS, ...options }
  );
};

export const usePendingWorkspaceMembers = (
  params: MembersParams,
  options?: SWRConfiguration & { skip?: boolean }
) => {
  return useSWR<PendingIdentitySchema[], MembersParams, never>(
    options?.skip
      ? null
      : {
          url: `${apiWorkspacesPath}/current/members/pending`,
          method: 'GET',
          params,
        },
    { ...DEFAULT_OPTIONS, ...options }
  );
};
